import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import Loader from "../../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import { Grid2, Box, TextField, Typography } from "@mui/material";
import StyledGridComp from "../../components/StyledGridComp";
import { formatDate, notifyError, notifySuccess } from "../../utilities/helper";
import {
    fetchAllReferenceMaterialsService,
    deleteReferenceMaterialService,
    fetchReferenceMaterialOnloadData,
    addReferenceMaterialService
} from "../../services/referenceMaterialService";
import SearchedComponent from "../../components/SearchedComponent";
import useDebounceSearch from "../../hooks/useDebounceSearch";
import { Home } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import SearchableSelect from "../../components/SearchableSelect";

const ReferenceMaterialManagement = () => {
    const [load, setLoad] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const [rowData, setRowData] = useState([]);
    const [rowLength, setRowLength] = useState(0);
    const [searchedValue, setSearchedValue] = useState("");
    const navigate = useNavigate();

    const [typeOptions, setTypeOptions] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    const debounceQuery = useDebounceSearch(searchedValue, 500);

    const access_token = localStorage?.getItem("access_token");
    const roleId = localStorage.getItem("roleId");
    const email = localStorage.getItem("email");
    const cultureCode = localStorage.getItem("currentUserCultureCode");

    const createRefSchema = yup.object().shape({
        name: yup.string().required("Name is required"),
        type: yup.string().required("Type is required"),
    });

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(createRefSchema),
        defaultValues: {
            name: "",
            type: "",
        },
    });

    useEffect(() => {
        getOnloadData();
    }, []);

    const getOnloadData = async () => {
        try {
            const res = await fetchReferenceMaterialOnloadData(access_token, null, roleId, cultureCode);
            if (res?.status === 200) {
                setTypeOptions(res?.data?.result?.viewBagData?.referenceMaterialTypeSelectList || []);
            }
        } catch (err) {
            console.log(err.message);
        }
    };

    useEffect(() => {
        if (page >= 0 && pageSize && access_token) {
            getAllReferenceMaterials(pageSize, page, access_token, debounceQuery);
        }
    }, [page, pageSize, access_token, debounceQuery]);

    const getAllReferenceMaterials = async (
        pageSize,
        page,
        access_token,
        debouncedSearch = "",
    ) => {
        setLoad(true);
        try {
            const res = await fetchAllReferenceMaterialsService(
                page + 1,
                pageSize,
                access_token,
                debouncedSearch,
            );
            if (res?.status === 200) {
                setLoad(false);
                setRowData(res?.data?.result || []);
                setRowLength(res?.data?.totalCount || 0);
            } else {
                setLoad(false);
            }
        } catch (err) {
            setLoad(false);
            notifyError(err?.message || "Error fetching data");
            if (err?.status === 401) {
                navigate("/unauthorize");
            }
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            const extension = file.name.split('.').pop().toLowerCase();
            if (extension !== 'pdf' && extension !== 'mp4') {
                notifyError("Only .pdf and .mp4 files are allowed.");
                setSelectedFile(null);
                fileInputRef.current.value = "";
                return;
            }
            setSelectedFile(file);
        }
    };

    const onSubmit = async (data) => {
        if (!selectedFile) {
            notifyError("Please select a file to upload.");
            return;
        }

        setLoad(true);
        const extension = selectedFile.name.split('.').pop().toLowerCase();
        const fileNameWithoutExt = selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.'));
        const contentType = extension === 'pdf' ? 'application/pdf' : 'video/mp4;application/octet-stream';

        const formData = new FormData();
        formData.append("UserId", roleId || "");
        formData.append("ReferenceMaterialTypeId", data.type);
        formData.append("Name", data.name);
        formData.append("File", selectedFile);
        formData.append("FileName", fileNameWithoutExt);
        formData.append("ContentType", contentType);

        try {
            const res = await addReferenceMaterialService(access_token, formData, email || "");
            console.log(res)
            if (res?.data.statusCode === 200) {
                setLoad(false);
                notifySuccess("Reference Material uploaded successfully!");
                reset();
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                getAllReferenceMaterials(pageSize, page, access_token, debounceQuery);
            } else {
                setLoad(false);
                notifyError("Upload failed: Name or file already exists.");
            }
        } catch (err) {
            setLoad(false);
            notifyError(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this reference material?")) {
            setLoad(true);
            try {
                const res = await deleteReferenceMaterialService(access_token, id);
                if (res?.status === 200 || res?.data?.status === "Success" || res?.data?.message === "Success" || res?.data?.status === "success") {
                    notifySuccess("Deleted successfully!");
                    getAllReferenceMaterials(pageSize, page, access_token, debounceQuery);
                } else {
                    setLoad(false);
                    notifyError(res?.data?.message || "Failed to delete.");
                }
            } catch (err) {
                setLoad(false);
                notifyError(err?.message || "Error deleting data");
            }
        }
    };

    const columns = useMemo(() => [
        {
            field: "name",
            headerName: "Name",
            flex: 1, minWidth: 200,
            renderHeader: () => <div className="grid-heading">{"Name"}</div>,
            renderCell: (params) => {
                return <span className="data-font">{params.row.name}</span>;
            },
        },
        {
            field: "username",
            headerName: "User",
            flex: 1, minWidth: 200,
            renderHeader: () => <div className="grid-heading">{"User"}</div>,
            renderCell: (params) => {
                return <span className="data-font">{params.row.username}</span>;
            },
        },
        {
            field: "referenceMaterialType",
            headerName: "Type",
            flex: 1, minWidth: 150,
            renderHeader: () => <div className="grid-heading">{"Type"}</div>,
            renderCell: (params) => {
                return <span className="data-font">{params.row.referenceMaterialType}</span>;
            },
        },
        {
            field: "createdTime",
            headerName: "Created Time",
            flex: 1, minWidth: 200,
            renderHeader: () => <div className="grid-heading">{"Created Time"}</div>,
            renderCell: (params) => {
                return (
                    <span className="data-font">
                        {params.row.createdTime ? formatDate(params.row.createdTime) : null}
                    </span>
                );
            },
        },
        {
            field: "action",
            headerName: "Action",
            width: 150,
            renderHeader: () => <div className="grid-heading">{"Action"}</div>,
            renderCell: (params) => {
                return (
                    <button
                        style={{ color: "#00b0f0", background: "none", border: "none", cursor: "pointer", textDecoration: "none", fontWeight: "bold" }}
                        onClick={() => handleDelete(params.row.id)}
                    >
                        Delete
                    </button>
                );
            },
        },
    ], [page, pageSize, access_token, debounceQuery]);

    const handleSearch = useCallback((e) => {
        setSearchedValue(e.target.value);
    }, []);

    return (
        <div className="content-body">
            <div className="home-container">
                <p
                    className="bread-crumbs-link-container"
                    style={{ fontWeight: "600" }}
                >
                    <Link className="bread-crumbs-link" to="/">
                        Home
                    </Link>{" "}
                    |{" "}
                    <Link className="bread-crumbs-link" to="/upload-reference-materials">
                        Upload Reference Material
                    </Link>
                    -{" "}
                    <b style={{ color: "gray" }}>{localStorage?.getItem("studyName")}</b>
                </p>
                <Link className="bread-crumbs-link home-inner-container">
                    Set as Home Page
                    <span>
                        <Home
                            sx={{
                                fontSize: "20px",
                            }}
                        />
                    </span>
                </Link>
            </div>

            <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto", marginBottom: "20px" }}
            >
                <Grid2 item md={12}>
                    <p className="page-heading">Upload Reference Material</p>
                </Grid2>
            </Grid2>

            {/* Upload Form Section */}
            <Box
                component="form"
                autoComplete="off"
                sx={{ height: "auto", width: "100%", marginBottom: "40px", padding: "20px", backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid2
                    container
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    sx={{ width: "100%", margin: "0 auto" }}
                    gap={3}
                >
                    <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                        <div>
                            <p className="labelStyles">
                                Name
                                <span>*</span>
                            </p>
                            <TextField
                                size="small"
                                fullWidth
                                type="text"
                                placeholder="New Reference Material"
                                {...register("name")}
                            />
                        </div>
                        {errors.name && (
                            <div className="error-container">{errors.name.message}</div>
                        )}
                    </Grid2>

                    <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                        <div>
                            <p className="labelStyles">
                                Type
                                <span>*</span>
                            </p>
                            <SearchableSelect
                                control={control}
                                data={typeOptions}
                                name={"type"}
                                optionKey="id"
                                optionLabel="name"
                            />
                        </div>
                        {errors.type && (
                            <div className="error-container">{errors.type.message}</div>
                        )}
                    </Grid2>

                    <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                        <div>
                            <p className="labelStyles">User</p>
                            <TextField
                                size="small"
                                disabled={true}
                                fullWidth
                                type="text"
                                value={email || ""}
                                sx={{ backgroundColor: "#f0f0f0" }}
                            />
                        </div>
                    </Grid2>

                    <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                        <div>
                            <p className="labelStyles">
                                File Upload
                                <span>*</span>
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', height: '40px' }}>
                                <button
                                    type="button"
                                    className="create-btn"
                                    style={{ padding: '0 20px', height: '100%', background: '#00b0f0', border: 'none', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    {selectedFile ? "Change File" : "+ Add File"}
                                </button>
                                {selectedFile && (
                                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                                        Selected: {selectedFile.name}
                                    </Typography>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                                accept=".pdf,.mp4"
                            />
                        </div>
                    </Grid2>
                </Grid2>

                <div style={{ marginTop: "20px" }}>
                    <button type="submit" className="create-btn" style={{ width: '120px' }}>
                        + Add New
                    </button>
                </div>
            </Box>

            {/* Table Section */}
            <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto", marginBottom: "10px" }}
            >
                <Grid2 item md={8}>
                    <div className="search-bar-container" style={{ justifyContent: 'flex-start' }}>
                        <SearchedComponent
                            searchedValue={searchedValue}
                            handleSearch={handleSearch}
                            placeholder={"Search Name"}
                        />
                    </div>
                </Grid2>
            </Grid2>

            <StyledGridComp
                rows={rowData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={rowLength}
            ></StyledGridComp>
        </div>
    );
};

export default ReferenceMaterialManagement;
